/* eslint-disable import/no-cycle */
import * as React from 'react';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { getDateWithDayOffset, areSameDates, getISOWeekNum } from '../../utils/Date';
import { DatePickerCell, DatePickerCellProps } from './DatePickerCell';
import { datePickerCellStyles } from './DatePickerStyles';

interface DatePickerRowProps {
  bodyHasFocus: boolean;
  focusedDate: Date;
  isDateDisabled?: (date: Date) => boolean;
  onChange: (date: Date) => void;
  onFocusedDateChange: (date: Date) => void;
  selectedDate?: Date;
  showWeekNumbers?: boolean;
  startDate: Date;
}

class DatePickerRow extends React.PureComponent<DatePickerRowProps, {}> {
  private getCell(index: number): JSX.Element {
    const cellDate = getDateWithDayOffset(this.props.startDate, index);
    const isDisabled = this.props.isDateDisabled && this.props.isDateDisabled(cellDate);
    const isInCurrMonth = cellDate.getMonth() === this.props.focusedDate.getMonth();
    const isSelected = areSameDates(cellDate, this.props.selectedDate);
    const isToday = areSameDates(cellDate, new Date());
    const isTabbable = areSameDates(cellDate, this.props.focusedDate);
    const isFocused = isTabbable && this.props.bodyHasFocus;

    const handleFocus = () => this.props.onFocusedDateChange(cellDate);
    const handleClick: React.MouseEventHandler = () => {
      if (!isInCurrMonth) {
        this.props.onFocusedDateChange(cellDate);
      }
      if (!isDisabled) {
        this.props.onChange(cellDate);
      }
    };

    const dateLabel = Formatters.formatFullDate(cellDate);
    const weekNumLabel = this.props.showWeekNumbers ?
      `${Messages.weekNumber({ weekNum: getISOWeekNum(this.props.startDate).toString() })}`
      : '';

    const cellProps: DatePickerCellProps = {
      'aria-label': `${dateLabel} ${weekNumLabel}`.trim(),
      isDisabled,
      isFocused,
      isInCurrMonth,
      isSelected,
      isTabbable,
      isToday,
      onClick: handleClick,
      onFocus: handleFocus,
    };
    return (
      <DatePickerCell key={index} {...cellProps}>
        {cellDate.getDate()}
      </DatePickerCell>
    );
  }

  public render(): JSX.Element {
    const cells: JSX.Element[] = [];
    const daysOfWeekNum = 7;
    for (let i = 0; i < daysOfWeekNum; i++) {
      cells.push(this.getCell(i));
    }
    if (this.props.showWeekNumbers) {
      const currISOWeekNum = getISOWeekNum(this.props.startDate);
      const weekNumLabel = Messages.weekNumber({ weekNum: currISOWeekNum.toString() });
      const weekNumCellProps: React.HTMLAttributes<HTMLElement> = {
        style: datePickerCellStyles.isoWeekNum,
        'aria-label': weekNumLabel,
        role: 'gridcell'
      };
      cells.unshift(
        <div {...weekNumCellProps} key='iso-week'>
          {currISOWeekNum}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex' }} role='row'>
        {cells}
      </div>
    );
  }
}

export { DatePickerRowProps, DatePickerRow };
